import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import useBatterySignalHistory from '../hooks/useBatterySignalHistory';
import HistoryRangeSelector from './HistoryRangeSelector';
import { HISTORY_RANGES, DEFAULT_HISTORY_RANGE } from '../constants/historyRanges';

const COLOR_BATTERY = 'rgba(16, 185, 129, 1)';
const COLOR_RSSI = 'rgba(139, 92, 246, 1)';

const getAreaGradient = (color) => ({
        color: {
                type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                        { offset: 0, color: color.replace('1)', '0.3)') },
                        { offset: 1, color: color.replace('1)', '0.02)') }
                ]
        }
});

const axisNameStyle = { fontWeight: 600, fontSize: 13, fontFamily: 'Lexend', align: 'center' };

const hasPoints = (series) => Array.isArray(series) && series.some(([, value]) => value !== null && value !== undefined);

const DeviceBatteryRssiChart = ({ deviceId }) => {
        const [range, setRange] = useState(DEFAULT_HISTORY_RANGE);
        const { series, firstReadingAt, loading, error } = useBatterySignalHistory(deviceId, range);

        const batteryValues = series.battery;
        const rssiValues = series.rssi;
        const isEmpty = !hasPoints(batteryValues) && !hasPoints(rssiValues);
        const selectedRange = HISTORY_RANGES.find((r) => r.key === range);

        const option = useMemo(() => ({
                grid: { top: 50, bottom: 60, left: 55, right: 65 },
                legend: { top: 8, data: ['Battery', 'Signal'] },
                xAxis: { type: 'time', splitLine: { show: true, lineStyle: { type: 'dashed' } } },
                yAxis: [
                        {
                                type: 'value',
                                min: 0,
                                max: 100,
                                name: 'Battery (%)',
                                position: 'left',
                                nameRotate: 90,
                                nameLocation: 'middle',
                                nameGap: 38,
                                nameTextStyle: axisNameStyle
                        },
                        {
                                type: 'value',
                                // Signal strength is negative; never anchor this axis at 0
                                // or the usable -40..-80 band collapses into a sliver.
                                min: -100,
                                max: -30,
                                name: 'Signal (dBm)',
                                position: 'right',
                                // Share split lines with the left axis instead of drawing a second grid.
                                alignTicks: true,
                                splitLine: { show: false },
                                nameRotate: 90,
                                nameLocation: 'middle',
                                nameGap: 45,
                                nameTextStyle: axisNameStyle
                        }
                ],
                tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'cross' }
                },
                series: [
                        {
                                name: 'Battery',
                                type: 'line',
                                yAxisIndex: 0,
                                data: batteryValues,
                                smooth: true,
                                showSymbol: false,
                                connectNulls: false,
                                itemStyle: { color: COLOR_BATTERY },
                                areaStyle: getAreaGradient(COLOR_BATTERY),
                                tooltip: { valueFormatter: (value) => (value === null || value === undefined ? '-' : `${value} %`) }
                        },
                        {
                                name: 'Signal',
                                type: 'line',
                                yAxisIndex: 1,
                                data: rssiValues,
                                smooth: true,
                                showSymbol: false,
                                connectNulls: false,
                                itemStyle: { color: COLOR_RSSI },
                                // No area fill: on a negative axis it would fill downwards
                                // and collide with the battery area.
                                tooltip: { valueFormatter: (value) => (value === null || value === undefined ? '-' : `${value} dBm`) }
                        }
                ],
                dataZoom: [
                        { type: 'inside', xAxisIndex: 0, start: 0, end: 100 },
                        { type: 'slider', xAxisIndex: 0, start: 0, end: 100, bottom: 10 }
                ]
        }), [batteryValues, rssiValues]);

        // Top right of the chart block, matching Trends & Analytics. It stays
        // mounted across loading and error states so switching range never takes
        // the buttons away from under the pointer.
        const rangeSelector = (
                <div className="flex justify-end mb-3">
                        <HistoryRangeSelector
                                value={range}
                                onChange={setRange}
                                firstReadingAt={firstReadingAt}
                                idPrefix="battery-signal-range"
                        />
                </div>
        );

        const body = () => {
                if (loading) {
                        return <div className="flex items-center justify-center h-[380px] text-text-weak">Loading...</div>;
                }

                if (error) {
                        return <div className="flex items-center justify-center h-[380px] text-status-critical">{error}</div>;
                }

                if (isEmpty) {
                        return (
                                <div className="flex flex-col items-center justify-center h-[380px] text-center" data-testid="battery-signal-empty">
                                        <p className="text-sm text-text-weak">No battery or signal data in {selectedRange?.description}.</p>
                                        <p className="text-xs text-text-weak mt-1">These readings are only recorded by firmware that reports them.</p>
                                </div>
                        );
                }

                return <ReactECharts option={option} notMerge={true} style={{ height: '380px', width: '100%' }} />;
        };

        return (
                <div>
                        {rangeSelector}
                        {body()}
                </div>
        );
};

export default DeviceBatteryRssiChart;
